import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getAuthDiagnostics,
  repairMissingProfiles,
  sendAccountInvite,
  type AuthDiagnostics,
} from "@/lib/auth-diagnostics.functions";
import {
  getEmailLiveStatus,
  sendAdminTestEmail,
  sendSelfPasswordResetTest,
  type EmailLiveStatus,
} from "@/lib/email-diagnostics.functions";

export const Route = createFileRoute("/_authenticated/admin/auth-email")({
  head: () => ({
    meta: [
      { title: "Auth & Email diagnostics · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthEmailAdmin,
});


function Stat({ label, value, tone }: { label: string; value: number; tone?: "warn" }) {
  return (
    <div className="rounded-lg border hairline bg-white/[0.02] p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className={`mt-2 font-serif text-3xl ${tone === "warn" && value > 0 ? "text-amber-300" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function AuthEmailAdmin() {
  const load = useServerFn(getAuthDiagnostics);
  const repair = useServerFn(repairMissingProfiles);
  const invite = useServerFn(sendAccountInvite);
  const loadLive = useServerFn(getEmailLiveStatus);
  const testEmail = useServerFn(sendAdminTestEmail);
  const testReset = useServerFn(sendSelfPasswordResetTest);

  const [data, setData] = useState<AuthDiagnostics | null>(null);
  const [live, setLive] = useState<EmailLiveStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState<"invite" | "recovery">("invite");

  async function refresh() {
    setError(null);
    try {
      const [diag, liveStatus] = await Promise.all([load({}), loadLive({})]);
      setData(diag);
      setLive(liveStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load diagnostics");
    }
  }

  async function runTest(which: "email" | "reset") {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const res =
        which === "email"
          ? await testEmail({})
          : await testReset({ data: { redirectTo: `${window.location.origin}/reset-password` } });
      setNotice(`${res.ok ? "Accepted" : "Failed"} → ${res.target}: ${res.detail}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed");
    } finally {
      setBusy(false);
    }
  }


  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onRepair() {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const res = await repair({});
      setNotice(`Profiles repaired: ${res.repaired}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Repair failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const res = await invite({
        data: {
          email: email.trim().toLowerCase(),
          kind,
          redirectTo:
            kind === "invite"
              ? `${window.location.origin}/account`
              : `${window.location.origin}/reset-password`,
        },
      });
      setNotice(`Accepted by the auth provider (${res.sent}). Delivery is not guaranteed — confirm receipt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-serif text-3xl">Auth &amp; Email diagnostics</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Account lifecycle health, newsletter-vs-account split, and email deliverability status.
        </p>
      </header>

      {error && (
        <p className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      )}
      {notice && (
        <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          {notice}
        </p>
      )}

      {!data && !error && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && (
        <>
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Auth users" value={data.authUsers} />
            <Stat label="Unconfirmed" value={data.unconfirmed} tone="warn" />
            <Stat label="Never signed in" value={data.neverSignedIn} tone="warn" />
            <Stat label="Admins" value={data.admins} tone="warn" />
            <Stat label="Profiles" value={data.profiles} />
            <Stat label="Missing profiles" value={data.missingProfiles} tone="warn" />
            <Stat label="Newsletter subscribers" value={data.subscribers} />
            <Stat label="Newsletter-only" value={data.newsletterOnly} />
          </section>

          {live && (
            <section
              className={`rounded-lg border p-5 ${
                live.dns.txtPresent && live.dns.nsPresent
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-amber-500/30 bg-amber-500/5"
              }`}
            >
              <h2 className="font-serif text-xl">Email delivery (live probe)</h2>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Sender domain · {live.senderDomain} · checked{" "}
                {new Date(live.checkedAt).toLocaleString()}
              </p>
              <dl className="mt-3 space-y-1 text-sm text-muted-foreground">
                <div>
                  <span className="text-foreground">TXT {live.dns.verifyHost}:</span>{" "}
                  <span className={live.dns.txtPresent ? "text-emerald-300" : "text-amber-300"}>
                    {live.dns.txtPresent ? "present" : "MISSING"}
                  </span>
                </div>
                <div>
                  <span className="text-foreground">NS {live.dns.nsHost}:</span>{" "}
                  <span className={live.dns.nsPresent ? "text-emerald-300" : "text-amber-300"}>
                    {live.dns.nsPresent
                      ? live.dns.nsFound.join(", ")
                      : `MISSING — expected ${live.dns.expectedNs.join(" + ")}`}
                  </span>
                </div>
                <div>
                  <span className="text-foreground">Email API key:</span>{" "}
                  {live.api.keyPresent ? "configured" : "missing"}
                </div>
              </dl>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => runTest("email")}
                  disabled={busy}
                  className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold disabled:opacity-50"
                >
                  Send test app email to myself
                </button>
                <button
                  type="button"
                  onClick={() => runTest("reset")}
                  disabled={busy}
                  className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold disabled:opacity-50"
                >
                  Send password-reset test to myself
                </button>
              </div>
            </section>
          )}

          {live && (
            <section className="rounded-lg border hairline p-5">
              <h2 className="font-serif text-xl">Auth configuration (live)</h2>
              <dl className="mt-3 space-y-1 text-sm text-muted-foreground">
                <div>
                  <span className="text-foreground">Supabase Site URL:</span>{" "}
                  {live.siteUrl.readable
                    ? live.siteUrl.value
                    : "not exposed by the auth API — set it in Cloud → Users → Auth settings"}{" "}
                  <span className="text-xs">(target: {live.siteUrl.expected})</span>
                </div>
                <div>
                  <span className="text-foreground">Redirect targets:</span> /account (confirmation),
                  /reset-password (recovery) — resolved from the current origin, so preview,
                  buddhaka.com and www both work.
                </div>
              </dl>
            </section>
          )}

          {live && live.lastTests.length > 0 && (
            <section className="rounded-lg border hairline p-5">
              <h2 className="font-serif text-xl">Recent delivery tests</h2>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {live.lastTests.map((t) => (
                  <li key={t.created_at}>
                    <span className={t.ok ? "text-emerald-300" : "text-amber-300"}>
                      {t.ok ? "accepted" : "failed"}
                    </span>{" "}
                    · {t.kind} · {t.target} · {new Date(t.created_at).toLocaleString()}
                    {t.detail ? ` — ${t.detail}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}


          {data.recentAuthEmailFailures.length > 0 && (
            <section className="rounded-lg border hairline p-5">
              <h2 className="font-serif text-xl">Accounts stuck unconfirmed</h2>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {data.recentAuthEmailFailures.map((f) => (
                  <li key={f.email}>
                    <span className="text-foreground">{f.email}</span> — {f.issue}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-lg border hairline p-5">
            <h2 className="font-serif text-xl">Repair &amp; invite</h2>
            <button
              type="button"
              onClick={onRepair}
              disabled={busy}
              className="mt-3 rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold disabled:opacity-50"
            >
              Repair missing profiles
            </button>

            <form onSubmit={onSend} className="mt-6 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Action</span>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as "invite" | "recovery")}
                  className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
                >
                  <option value="invite">Invite to create an account (newsletter-only upgrade)</option>
                  <option value="recovery">Send password reset</option>
                </select>
              </label>
              <p className="text-xs text-muted-foreground">
                No password is ever set on a subscriber's behalf — the recipient chooses their own.
              </p>
              <button
                type="submit"
                disabled={busy}
                className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send"}
              </button>
            </form>
          </section>
        </>
      )}
    </div>
  );
}
